import React, { useState, useEffect } from "react";
import axios from "axios";

const url = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com/api";
const stallID = localStorage.getItem("stallID");
const username = localStorage.getItem("username");

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236c757d'%3ENo Image%3C/text%3E%3C/svg%3E";

function FoodsProfile() {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [foodNo, setFoodNo] = useState("");
  const [foodName, setFoodName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(0.0);
  const [prom_price, setPromPrice] = useState(0.0);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(PLACEHOLDER);
  const [editingID, setEditingID] = useState(null);
  const [message, setMessage] = useState("");

  // UPDATED: Use /food-images endpoint instead of /uploads
  const getImageUrl = (imageURL) => {
    console.log("🔍 getImageUrl called with:", imageURL);

    if (!imageURL || imageURL === "null" || imageURL === "undefined") {
      console.log("🔍 No imageURL provided, using placeholder");
      return PLACEHOLDER;
    }

    // If it's already a full URL or data URL, return as is
    if (imageURL.startsWith('http') || imageURL.startsWith('data:')) {
      console.log("🔍 Already full URL, returning as is:", imageURL);
      return imageURL;
    }

    // Remove any leading slashes and trim
    const cleanImageURL = imageURL.replace(/^\//, '').trim();
    console.log("🔍 Cleaned imageURL:", cleanImageURL);

    // UPDATED: Extract just the filename from the path
    let filename;
    if (cleanImageURL.includes('/')) {
      // If path contains folders, extract just the filename
      filename = cleanImageURL.split('/').pop();
      console.log("🔍 Extracted filename:", filename);
    } else {
      filename = cleanImageURL;
    }

    // UPDATED: Build URL using /food-images endpoint
    const finalUrl = `${url}/food-images/${filename}`;
    console.log("🔍 Final image URL:", finalUrl);

    return finalUrl;
  };

  // Alternative simpler version if you prefer:
  // const getImageUrl = (imageURL) => {
  //   if (!imageURL) return PLACEHOLDER;
  //   if (imageURL.startsWith('http') || imageURL.startsWith('data:')) return imageURL;
  //
  //   // Extract filename from path (handles both "filename.png" and "uploads/filename.png")
  //   const filename = imageURL.split('/').pop();
  //   return `${url}/food-images/${filename}`;
  // };

  // Fetch foods
  const fetchFoods = async () => {
    try {
      const res = await axios.get(`${url}/api/admin/foods`, { params: { stallID } });
      console.log("📦 Raw foods data from API:", res.data);

      setFoods(res.data);
    } catch (err) {
      console.error("❌ Error fetching foods:", err);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${url}/api/admin/category`, { params: { stallID } });
      setCategories(res.data.categories);
    } catch (err) {
      console.error("❌ Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchFoods();
    fetchCategories();
  }, []);

  // Submit (add/update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("stallID", stallID);
      formData.append("category", category);
      formData.append("foodNo", foodNo);
      formData.append("foodName", foodName);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("prom_price", prom_price);
      if (image) formData.append("image", image);

      if (editingID) {
        await axios.put(`${url}/api/admin/foods/${editingID}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("✅ Food updated successfully!");
      } else {
        await axios.post(`${url}/api/admin/foods`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("✅ Food added successfully!");
      }

      resetForm();
      fetchFoods();
    } catch (err) {
      console.error("❌ Save food error:", err);
      setMessage("⚠️ Server error");
    }
  };

  // Edit function with cache busting
  const handleEdit = (food) => {
    console.log("📝 EDITING FOOD - Full object:", food);
    console.log("📝 Food imageURL:", food.imageURL);

    setEditingID(food.id);
    setCategory(food.category || "");
    setFoodNo(food.foodNo)
    setFoodName(food.foodName);
    setDescription(food.description);
    setPrice(food.price);
    setPromPrice(food.prom_price || "");
    setImage(null);

    // Handle image preview with cache busting
    if (food.imageURL) {
      let imageUrl = getImageUrl(food.imageURL);
      // Add timestamp to prevent caching issues
      imageUrl += `${imageUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`;
      console.log("🖼️ Setting preview URL:", imageUrl);
      setPreview(imageUrl);
    } else {
      console.log("🖼️ No imageURL, using placeholder");
      setPreview(PLACEHOLDER);
    }
  };

  // Delete food
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this food item?")) return;

    try {
      await axios.delete(`${url}/api/admin/foods/${id}`);
      setMessage("🗑️ Food deleted successfully");
      fetchFoods();
    } catch (err) {
      console.error("❌ Delete error:", err);
      setMessage("⚠️ Error deleting food");
    }
  };

  // Reset form
  const resetForm = () => {
    setEditingID(null);
    setCategory("");
    setFoodNo("");
    setFoodName("");
    setDescription("");
    setPrice(0.0);
    setPromPrice(0.0);
    setImage(null);
    setPreview(PLACEHOLDER);
    setMessage("");
  };

  return (
    <div className="container mt-4">
      <h2>🍲 Foods Profile</h2>
      <p>
        Stall ID: <strong>{stallID}</strong> | Stall Name: <strong>{username}</strong>
      </p>

      {/* Debug Info */}
      <div className="alert alert-info">
        <strong>Updated:</strong> Now using /food-images endpoint for images
      </div>

      {/* Form */}
      <div className="card p-3 mb-4 shadow">
        <h5>{editingID ? "✏️ Edit Food Item" : "➕ Add New Food Item"}</h5>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-bold">Category *</label>
                <select
                  className="form-control"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.categoryName}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
              </div>
   <div className="mb-3">
                <label className="fw-bold">Food ID *</label>
                <input
                  type="text"
                  className="form-control"
                  value={foodNo}
                  onChange={(e) => setFoodNo(e.target.value.toUpperCase())}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="fw-bold">Food Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={foodName}
                  onChange={(e) => setFoodName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="fw-bold">Description *</label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  required
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-3">
                <label className="fw-bold">Price ($) *</label>
                <input
                  type="number"
                  className="form-control"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value))}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="fw-bold">Promotion Price ($)</label>
                <input
                  type="number"
                  className="form-control"
                  value={prom_price}
                  onChange={(e) => setPromPrice(parseFloat(e.target.value))}
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="mb-3">
                <label className="fw-bold">
                  Food Image {!editingID && '*'}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setImage(file);
                    if (file) {
                      const objectUrl = URL.createObjectURL(file);
                      console.log("📸 New file selected, preview URL:", objectUrl);
                      setPreview(objectUrl);
                    } else if (editingID) {
                      console.log("📸 File cleared, keeping current preview");
                    } else {
                      console.log("📸 No file, using placeholder");
                      setPreview(PLACEHOLDER);
                    }
                  }}
                />
                <small className="text-muted">
                  {!editingID
                    ? "Required for new items"
                    : "Select new image to update current one"
                  }
                </small>
              </div>
            </div>
          </div>

          {/* Image Preview */}
          <div className="mb-3">
            <label className="fw-bold">Image Preview</label>
            <div className="border rounded p-3 text-center bg-light">
              <img
                src={preview}
                alt="Food preview"
                style={{
                  maxWidth: "200px",
                  maxHeight: "150px",
                  objectFit: "contain",
                  border: "2px dashed #ccc",
                  backgroundColor: "#f8f9fa"
                }}
                className="img-fluid"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.log("❌ IMAGE ERROR - Preview failed to load:", preview);
                  e.target.src = PLACEHOLDER;
                  setPreview(PLACEHOLDER);
                }}
                onLoad={(e) => {
                  console.log("✅ IMAGE SUCCESS - Preview loaded successfully");
                }}
              />
              <div className="mt-2">
                <small className={preview === PLACEHOLDER ? "text-danger" : "text-success"}>
                  {preview === PLACEHOLDER
                    ? "No image selected"
                    : image
                      ? "New image ready for upload"
                      : "Current food image"
                  }
                </small>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary">
              {editingID ? "Update Food" : "Add Food"}
            </button>
            {editingID && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        {message && (
          <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-danger'} mt-3`}>
            {message}
          </div>
        )}
      </div>

      {/* Foods Table */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Food Items ({foods.length})</h5>
          <small className="text-muted">
            Using /food-images endpoint
          </small>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Food ID</th>
                  <th>Food Name</th>
                  <th>Image</th>
                  <th>Database Path</th>
                  <th>Final URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {foods.length > 0 ? (
                  foods.map((food) => (
                    <tr key={food.id}>
                      <td>{food.id}</td>
                      <td>{food.foodNo}</td>
                      <td>
                        <strong>{food.foodName}</strong>
                        <br />
                        <small className="text-muted">{food.category} - RM{food.price}</small>
                      </td>
                      <td className="text-center">
                        <div style={{ width: "80px", height: "60px" }} className="mx-auto">
                          <img
                            src={getImageUrl(food.imageURL) + `?t=${Date.now()}`}
                            alt={food.foodName}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "cover"
                            }}
                            className="img-thumbnail"
                            crossOrigin="anonymous"
                            onError={(e) => {
                              console.log(`❌ Table image failed for ${food.foodName}:`, food.imageURL);
                              e.target.src = PLACEHOLDER;
                            }}
                            onLoad={(e) => {
                              console.log(`✅ Table image loaded for ${food.foodName}`);
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          {food.imageURL || 'No image'}
                        </small>
                      </td>
                      <td>
                        <small>
                          <a href={getImageUrl(food.imageURL)} target="_blank" rel="noopener noreferrer">
                            Test Image
                          </a>
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-warning btn-sm me-1"
                          onClick={() => handleEdit(food)}
                          title="Edit food"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(food.id)}
                          title="Delete food"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div className="text-muted">
                        No food items found. Add your first food item above.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FoodsProfile;
